import React, { useState, useEffect, useRef } from 'react';
import styles from './CartesianGraph.module.scss';
import Toggle from '../../inputs/Toggle';

interface ChartData {
  group: string;
  series: { name: string; value: number }[];
}

/**
 * Courbe facultative tracée par-dessus les barres/lignes, sur son PROPRE axe
 * (à droite). Cet axe réutilise les traits horizontaux déjà dessinés pour
 * l'axe de gauche : pas de seconde grille, et les graduations de droite sont
 * choisies « rondes » (1, 2, 5 × 10ⁿ) pour tomber exactement sur ces traits.
 */
export interface SecondaryCurve {
  name   : string;
  /** Une valeur par groupe de `data`, dans le même ordre. */
  values : number[];
  color? : string;
  /**
   * Écart (courbe max / haut de l'axe de gauche) au-delà duquel l'axe de droite
   * devient un MULTIPLE ENTIER de celui de gauche (×2, ×5, ×10…), lisible d'un
   * coup d'œil : la graduation de droite est celle de gauche × k. En dessous, il
   * garde sa propre échelle « ronde ». Défaut : 3.
   */
  multipleThreshold? : number;
}

interface CartesianGraphProps {
  secondaryCurve?: SecondaryCurve;
  /** Intitulé de l'axe de gauche, centré sur son trait, au-dessus ; sur deux lignes s'il a plusieurs mots. */
  yAxisLabel?: string;
  data: ChartData[];
  width?: number;
  height?: number;
  xAxisLegendMargin?: number;
  title?: string;
  subTitle?: string;
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  titleFontSize?: number;
  subTitleFontSize?: number;
  xAxisLabelFontSize?: number;
  /** N'écrit sous l'axe que la première et la dernière étiquette, à l'horizontale : gagne en hauteur. */
  xAxisEndsOnly?: boolean;
  /** Étiquettes de bord imposées (p. ex. première et dernière lecture), tant que le bord correspondant est visible. */
  xAxisStartLabel?: string;
  xAxisEndLabel?: string;
  /** Prévenu quand la souris passe sur une abscisse (index du groupe), puis `null` quand elle la quitte. */
  onHoverGroup?: (index: number | null) => void;
  /** Prévenu quand on clique sur une abscisse (index du groupe). */
  onClickGroup?: (index: number) => void;
  /** Abscisses à éclairer (plus doucement que celle survolée), p. ex. quand on vise un point de la carte. */
  highlightIndices?: number[];
  /** Active le zoom à la molette (pivot = la souris, largeur du tracé inchangée). */
  zoomable?: boolean;
  /** Change : la fenêtre de zoom revient à toute la période (nouveau pas, autre graphe…). */
  zoomResetKey?: string | number;
  /** Lignes de la bulle sous la date, à la place du détail des séries (le cumul s'y ajoute toujours). */
  tooltipLines?: (index: number) => string[];
  /** Rempli par le graphe : de quoi piloter le zoom depuis des boutons (centre du tracé pour pivot). */
  /** Prévenu quand les limites du zoom changent : peut-on encore zoomer / dézoomer ? */
  onZoomLimits?: (limites: { peutZoomer: boolean; peutDezoomer: boolean }) => void;
  zoomApiRef?: React.MutableRefObject<{ avant: () => void; arriere: () => void; tout: () => void } | null>;
  yAxisLabelFontSize?: number;
  legendFontSize?: number;
  colors?: string[];
  textColor?: string;
  cumul?: boolean;
  grapheType?: 'Bar' | 'Line';
  showLegend?: boolean
  onVisibilityChange?: (visibility: boolean[]) => void;
    onClick?: (event: React.MouseEvent<SVGElement, MouseEvent>, id?: number, state?: boolean) => void;
  onMouseEnter?: (
    event: React.MouseEvent<SVGElement>,
    groupName?: string,
    seriesName?: string,
    value?: number
  ) => void;
  onMouseLeave?: (
    event: React.MouseEvent<SVGElement>,
    groupName?: string,
    seriesName?: string,
    value?: number
  ) => void;
}

const CartesianGraph2: React.FC<CartesianGraphProps> = ({
  secondaryCurve,
  yAxisLabel,
  data = [],
  width = 738,
  height = 316,
  xAxisLegendMargin = 40,
  title = "",
  subTitle = "",
  paddingTop = 50,
  paddingBottom = 50,
  paddingLeft = 50,
  paddingRight: paddingRightProp = 5,
  titleFontSize = 16,
  subTitleFontSize = 14,
  xAxisLabelFontSize = 12,
  xAxisEndsOnly = false,
  xAxisStartLabel,
  xAxisEndLabel,
  onHoverGroup,
  onClickGroup,
  highlightIndices,
  zoomable = false,
  zoomResetKey,
  tooltipLines,
  zoomApiRef,
  onZoomLimits,
  yAxisLabelFontSize = 12,
  legendFontSize = 14,
  colors = [
    "var(--color-2)", // ligne 1
    "var(--color-1)", // ligne 2
    "var(--color-3)", // ligne 3
    "var(--color-4)", // ligne 4
    "var(--color-5)"  // ligne 5
  ],
    textColor =  "var(--color-0)",
  cumul = false,
  grapheType = 'Bar',
  showLegend = true,
  onVisibilityChange,
    onClick,
  onMouseEnter,
  onMouseLeave,
}) => {


  // De la place pour les graduations de droite quand la courbe est présente.
  const paddingRight = secondaryCurve ? Math.max(paddingRightProp, 60) : paddingRightProp;

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Fenêtre visible, en indices de groupe FRACTIONNAIRES : [debut, debut + nb[. Par défaut tout.
  const [vue, setVue] = useState<{ debut: number; nb: number } | null>(null);
  const vueRef = useRef(vue);
  vueRef.current = vue;
  useEffect(() => { setVue(null); }, [zoomResetKey]);

  const nbTotal = data.length;
  const vNb     = vue ? Math.min(vue.nb, Math.max(nbTotal, 1)) : Math.max(nbTotal, 1);
  const vDebut  = vue ? Math.min(Math.max(0, vue.debut), Math.max(0, nbTotal - vNb)) : 0;
  const [, setTooltip] = useState<{ x: number; y: number; value: number; name: string } | null>(null);
  const [dimensions, setDimensions] = useState({ width, height });
  // Dérivées à chaque rendu (et non recopiées dans un état) : la marge de droite change
  // avec la courbe de cumul, et barres comme courbe doivent partager la même largeur utile.
  const availableWidth  = dimensions.width  - paddingLeft - paddingRight;
  const availableHeight = dimensions.height - paddingTop  - paddingBottom;
  const [seriesInvisibility, setSeriesInvisibility] = useState<boolean[]>(  data.length > 0 ? data[0].series.map(() => false) : [] );

  // Largeur d'un groupe et abscisse de son bord gauche, dans la fenêtre visible.
  const colW = availableWidth / vNb;
  const colX = (i: number): number => paddingLeft + (i - vDebut) * colW;
  const premierVisible = Math.max(0, Math.floor(vDebut));
  const dernierVisible = Math.min(nbTotal - 1, Math.ceil(vDebut + vNb) - 1);
  const clipId = useRef(`clip-${Math.random().toString(36).slice(2)}`).current;

  const graphRef = useRef<HTMLDivElement>(null);

  // `frac` : position du pivot dans le tracé (0 = bord gauche, 1 = bord droit).
  const zoomer = (facteur: number, frac: number) => {
    if (nbTotal < 2) return;

    const cur  = vueRef.current ?? { debut: 0, nb: nbTotal };
    const nb0  = Math.min(cur.nb, nbTotal);
    const d0   = Math.min(Math.max(0, cur.debut), Math.max(0, nbTotal - nb0));
    const pivot = d0 + frac * nb0;                      // le groupe (fractionnaire) sous le pivot

    const nb1  = Math.min(nbTotal, Math.max(Math.min(3, nbTotal), nb0 * facteur));
    const d1   = Math.min(Math.max(0, pivot - frac * nb1), nbTotal - nb1);

    setVue(nb1 >= nbTotal ? null : { debut: d1, nb: nb1 });
  };

  const peutZoomer   = nbTotal >= 2 && vNb > Math.min(3, nbTotal) + 1e-6;
  const peutDezoomer = vNb < nbTotal - 1e-6;
  useEffect(() => { onZoomLimits?.({ peutZoomer, peutDezoomer }); }, [peutZoomer, peutDezoomer]); // eslint-disable-line react-hooks/exhaustive-deps

  if (zoomApiRef) {
    zoomApiRef.current = { avant: () => zoomer(1 / 1.5, 0.5), arriere: () => zoomer(1.5, 0.5), tout: () => setVue(null) };
  }

  // Molette : zoom autour de la souris. Le groupe sous le curseur ne bouge pas, la
  // largeur du tracé non plus — seule la période affichée change. Écouteur natif
  // non passif : celui de React ne peut pas empêcher le défilement de la page.
  useEffect(() => {
    const svg = svgRef.current;
    if (!zoomable || !svg) return;

    const surMolette = (e: WheelEvent) => {
      const ctm = svg.getScreenCTM();
      if (!ctm || nbTotal < 2) return;
      e.preventDefault();

      const xSvg = (e.clientX - ctm.e) / ctm.a;
      const frac = Math.min(1, Math.max(0, (xSvg - paddingLeft) / availableWidth));

      zoomer(e.deltaY < 0 ? 1 / 1.2 : 1.2, frac);        // molette vers l'avant = zoom avant
    };

    svg.addEventListener('wheel', surMolette, { passive: false });
    return () => svg.removeEventListener('wheel', surMolette);
  }, [zoomable, nbTotal, paddingLeft, availableWidth]);

 useEffect(() => {
	
    const handleResize = () => {
      if (graphRef.current) {
        setDimensions({ width: graphRef.current.offsetWidth, height: graphRef.current.offsetHeight });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);

    if (graphRef.current) {
      resizeObserver.observe(graphRef.current);
    }

    handleResize();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);



useEffect(() => {
  if (
    data.length > 0 &&
    (seriesInvisibility.length === 0 ||
     seriesInvisibility.length !== data[0].series.length)
  ) {
    setSeriesInvisibility(data[0].series.map(() => false));
  }
}, [data]);

 const handleVisibilityChange = (index: number) => {
    const newInvisibility = [...seriesInvisibility];
    newInvisibility[index] = !newInvisibility[index];
    setSeriesInvisibility(newInvisibility);
    console.log ("G newInvisibility=", newInvisibility)	
	
    if (onVisibilityChange) {
      onVisibilityChange(newInvisibility);
    }
  };

let maxValue = 5;

if (cumul) {
  maxValue = 0;
  data.forEach((group) => {
    let cumulativeValue = 0;
    group.series.forEach((series, seriesIndex) => {
      // Ajouter la valeur de la série seulement si elle est visible
      if (!seriesInvisibility[seriesIndex]) {
        cumulativeValue += series.value;
      }
    });
    if (cumulativeValue > maxValue) {
      maxValue = cumulativeValue;
    }
  });
} else {
  // Filtrer les valeurs des séries en fonction de leur visibilité
  const visibleSeriesValues = data.flatMap(group =>
    group.series.filter((_, seriesIndex) => !seriesInvisibility[seriesIndex]).map(s => s.value)
  );

  // Calculer maxValue en utilisant uniquement les valeurs des séries visibles
  maxValue = visibleSeriesValues.length > 0 ? Math.max(...visibleSeriesValues) : 0;
}

if (maxValue < 5) {
  maxValue = 5;
}


//  const singleSeriesPerGroup = data.every(group => group.series.length === 1);



  /**
   * Intitulé centré sur un trait vertical (x), posé au-dessus de l'axe : sa
   * dernière ligne reste juste au-dessus du haut du graphe. Les mots se
   * répartissent sur deux lignes (moitié/moitié) quand il y en a plusieurs.
   */
  const renderAxisLabel = (text: string, x: number, color: string, key: string): JSX.Element => {
    const words = text.trim().split(/\s+/).filter(Boolean);
    const cut   = words.length > 1 ? Math.ceil(words.length / 2) : words.length;
    const lines = words.length > 1 ? [words.slice(0, cut).join(' '), words.slice(cut).join(' ')] : [text.trim()];
    const lineHeight = yAxisLabelFontSize * 1.2;

    return (
      <g key={key} className={styles.textGroup}>
        {lines.map((line, i) => (
          <text
            key        = {i}
            x          = {x}
            y          = {paddingTop - 18 - (lines.length - 1 - i) * lineHeight}
            textAnchor = "middle"
            fill       = {color}
            fontSize   = {`${yAxisLabelFontSize}px`}
            fontWeight = "bold"
          >
            {line}
          </text>
        ))}
      </g>
    );
  };

  const renderTitles = (): JSX.Element => {
    return (
      <g>
        <g className={styles.textGroup}>
          <text
            x={dimensions.width / 2}
            y={(paddingTop - titleFontSize - subTitleFontSize - 5) / 2 + titleFontSize / 2}
            textAnchor="middle"
            fontSize={`${titleFontSize}px`}
            fontWeight="bold"
            fill={textColor}
          >
            {title}
          </text>
        </g>
        <g className={styles.textGroup}>
          <text
            x={dimensions.width / 2}
            y={(paddingTop - titleFontSize - subTitleFontSize - 5) / 2 + titleFontSize + 5 + subTitleFontSize / 2}
            textAnchor="middle"
            fontSize={`${subTitleFontSize}px`}
            fill={textColor}
          >
            {subTitle}
          </text>
        </g>
      </g>
    );
  };

const renderLegend = (x: number, y: number): JSX.Element => {
  if (!showLegend) {
    return <></>;
  }

  let legendWidth = 110; // Augmenter la largeur pour inclure l'icône
  let legendHeight = 50;
  let legendNames = [];

  if (data.length > 0) {
    for (let i = 0; i < data[0].series.length; i++) {
      legendNames.push(data[0].series[i].name);
    }
    legendHeight = 15 + data[0].series.length * legendFontSize;

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={legendWidth}
          height={legendHeight}
          fill="none"
          stroke={textColor}
          strokeWidth="1"
        />
		
{legendNames.map((name, index) => {
  const lineHeight = legendFontSize + 6; // Ajoute un petit espacement
  const centerY = y + index * lineHeight + legendFontSize / 2 + 5;

  return (
    <g key={index} className={styles.textGroup}>
      <rect
        x={x + 5}
        y={centerY - legendFontSize / 2}
        width={legendFontSize - 3}
        height={legendFontSize - 3}
        fill={colors[index]}
        stroke="none"
        strokeWidth="1"
      />
      <text
        x={x + 20}
        y={centerY}
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={`${legendFontSize}px`}
        fontWeight="bold"
        fill={textColor}
      >
        {name}
      </text>
      <foreignObject
        x={x + legendWidth - 33}
        y={centerY - 12.5} // Toggle height = 25px
        width="30"
        height="25"
      >
        <Toggle
          height="25px"
          checked={!seriesInvisibility[index]}
          onChange={() => handleVisibilityChange(index)}
          form="switch"
        />
      </foreignObject>
    </g>
  );
})}

      </g>
    );
  } else {
    return <></>;
  }
};

  const renderAxes = (): JSX.Element => {
    return (
      <g>
        <line
          x1={paddingLeft - 2}
          y1={dimensions.height - paddingBottom}
          x2={dimensions.width - paddingRight}
          y2={dimensions.height - paddingBottom}
          stroke={textColor}
          strokeWidth="1"
        />
        <line
          x1={paddingLeft - 2}
          y1={paddingTop}
          x2={paddingLeft - 2}
          y2={dimensions.height - paddingBottom}
          stroke={textColor}
          strokeWidth="1"
        />
      </g>
    );
  };

  const renderYTicks = (): JSX.Element => {
    const yTicks = Array.from({ length: Math.ceil(maxValue) }, (_, i) => i + 1);
    const yTickSpacing = availableHeight / yTicks.length;
    const ySkipValues = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];

    let yLabelSkip = 1;
    for (const skip of ySkipValues) {
      if (yTickSpacing * skip < yAxisLabelFontSize * 1.5) {
        yLabelSkip = skip;
      } else {
        break;
      }
    }

    return (
      <g>
        {yTicks.map((tick, index) => {
          if (yLabelSkip > 1 && tick % yLabelSkip !== 0) return null;

          const y = dimensions.height - paddingBottom - (tick / maxValue) * availableHeight;
          return (
            <g key={index} className={styles.textGroup}>
              <line
                x1={paddingLeft - 2}
                y1={y}
                x2={dimensions.width - paddingRight}
                y2={y}
                stroke={textColor}
                strokeWidth="0.5"
              />
              <text
                x={paddingLeft - 10}
                y={y + 5}
                textAnchor="end"
                fill={textColor}
                fontSize={`${yAxisLabelFontSize}px`}
              >
                {tick}
              </text>
            </g>
          );
        })}
      </g>
    );
  };

  const renderXTicks = (): JSX.Element => {

    if (xAxisEndsOnly && data.length > 0) {
      const textY = dimensions.height - paddingBottom + 20;
      const bords = [
        { label: (vDebut <= 0 && xAxisStartLabel) || data[premierVisible].group,   x: paddingLeft,                  anchor: 'start' as const },
        ...(dernierVisible > premierVisible || (xAxisEndLabel && dernierVisible >= nbTotal - 1) ? [{ label: (dernierVisible >= nbTotal - 1 && xAxisEndLabel) || data[dernierVisible].group, x: paddingLeft + availableWidth, anchor: 'end' as const }] : []),
      ];
      return (
        <g>
          {bords.map((b, i) => (
            <g key={i} className={styles.textGroup}>
              <text x={b.x} y={textY} textAnchor={b.anchor} fill={textColor}
                    fontSize={`${xAxisLabelFontSize}px`} className={styles.text}>
                {b.label}
              </text>
            </g>
          ))}
        </g>
      );
    }

    const xTickSpacing = data.length > 0 ? availableWidth / data.length : availableWidth;
    // Largeur estimée de l'étiquette la plus longue (~0,6 em par caractère, plus
    // une marge) : un seuil fixe en « 5 caractères » laissait les dates longues
    // se superposer à l'horizontale.
    const longestLabel = data.reduce((max, d) => Math.max(max, String(d.group ?? '').length), 0);
    const canLabelsBeStraight = xTickSpacing >= xAxisLabelFontSize * Math.max(5, longestLabel * 0.6 + 1);

    let xLabelSkip = 1;
	
    if ( canLabelsBeStraight === false ) 
	 {
      for (let skip = 1; skip < data.length; skip++) 
	   {
        xLabelSkip = skip;

        if ( xTickSpacing * skip > xAxisLabelFontSize * 1.5) 
		 {
          break;
         }
       }
     }

    return (
      <g>
        {data.map((group, index) => {
			
          if (index % xLabelSkip !== 0) {
            return null;
          }

          const groupX = paddingLeft + index * xTickSpacing + (xTickSpacing * 0.8) / 2;
          const textY = dimensions.height - paddingBottom + 15;

          return (
            <React.Fragment key={index}>
              <g className={styles.textGroup}>
                <text
                  x={groupX}
                  y={textY}
                  textAnchor={canLabelsBeStraight ? 'middle' : 'end'}
                  fill={textColor}
                  fontSize={`${xAxisLabelFontSize}px`}
                  className={styles.text}
                  transform={canLabelsBeStraight ? '' : `rotate(-45 ${groupX} ${textY})`}
                >
                  {group.group}
                </text>
              </g>
              {!canLabelsBeStraight && xLabelSkip > 1 && (
                <line
                  x1={groupX}
                  y1={textY - 5}
                  x2={groupX}
                  y2={dimensions.height - paddingBottom}
                  stroke={textColor}
                  strokeWidth="0.5"
                />
              )}
            </React.Fragment>
          );
        })}
      </g>
    );
  };

  /** Passe l'abscisse survolée à l'état local et au parent. */
  const survoler = (index: number | null, e?: React.MouseEvent<SVGElement>) => {
    setHoverIndex(index);

    // Position du curseur dans le repère du SVG (il est mis à l'échelle par son viewBox).
    const ctm = svgRef.current?.getScreenCTM();
    if (e && ctm) {
      setHoverPos({ x: (e.clientX - ctm.e) / ctm.a, y: (e.clientY - ctm.f) / ctm.d });
    } else if (!e) {
      setHoverPos(null);
    }
    onHoverGroup?.(index);
  };

  /**
   * Une bande transparente par abscisse, sur toute la hauteur du tracé : on
   * survole « la date », pas seulement la barre (souvent minuscule), et la barre
   * comme le point de la courbe s'allument ensemble. Bulle : date, valeur, cumul.
   */
  const renderHover = (): JSX.Element => {
    if (data.length === 0) return <></>;

    const T = colW;
    const bandeY = paddingTop;
    const bandeH = Math.max(0, dimensions.height - paddingBottom - paddingTop);

    let bulle: JSX.Element | null = null;

    if (hoverIndex !== null && data[hoverIndex]) {
      const g = data[hoverIndex];
      const lignes: string[] = [String(g.group)];

      if (tooltipLines) {
        lignes.push(...tooltipLines(hoverIndex));
      } else {
        g.series.forEach((sr, i) => {
          if (!seriesInvisibility[i]) lignes.push(`${sr.name} : ${sr.value}`);
        });
      }

      if (secondaryCurve) lignes.push(`${secondaryCurve.name} : ${secondaryCurve.values[hoverIndex] ?? 0}`);

      const fs = Math.max(12, xAxisLabelFontSize - 1);
      const lh = fs * 1.4;
      const w  = Math.max(...lignes.map(l => l.length)) * fs * 0.58 + 20;
      const h  = lignes.length * lh + 12;
      const cx = hoverPos?.x ?? colX(hoverIndex) + T * 0.4;
      const cy = hoverPos?.y ?? bandeY + h + 16;
      // Juste au-dessus du curseur, centrée ; sous lui si le haut manque de place.
      const bx = Math.min(Math.max(0, cx - w / 2), dimensions.width - w);
      const by = cy - h - 14 >= 0 ? cy - h - 14 : cy + 18;

      bulle = (
        <g pointerEvents="none">
          <rect x={bx} y={by} width={w} height={h} rx={6} fill="rgba(13,27,42,0.95)" stroke="#8b5cf6" strokeWidth="1" />
          {lignes.map((l, i) => (
            <text key={i} x={bx + 10} y={by + 6 + (i + 1) * lh - lh * 0.3} fill={i === 0 ? '#ffffff' : (secondaryCurve && i === lignes.length - 1 ? secondaryCurve.color ?? '#f59e0b' : '#c8d6e5')}
                  fontSize={`${fs}px`} fontWeight={i === 0 ? 700 : 400}>
              {l}
            </text>
          ))}
        </g>
      );
    }

    return (
      <g>
        {data.map((_, i) => i < premierVisible || i > dernierVisible ? null : (
          <rect key={`hz-${i}`} x={Math.max(paddingLeft, colX(i))} y={bandeY}
                width={Math.max(0, Math.min(paddingLeft + availableWidth, colX(i) + T) - Math.max(paddingLeft, colX(i)))} height={bandeH}
                fill="transparent" style={onClickGroup ? { cursor: 'pointer' } : undefined}
                onMouseEnter={(e) => survoler(i, e)} onMouseMove={(e) => survoler(i, e)} onClick={() => onClickGroup?.(i)} />
        ))}
        {bulle}
      </g>
    );
  };

  const renderNoData = (): JSX.Element => {
    return (
      <g key={`no-data`} className={styles.textGroup}>
        <text
          x={paddingLeft + (availableWidth / data.length * 0.8) / 2}
          y={dimensions.height - paddingBottom + 15}
          textAnchor="middle"
          fontSize={`${xAxisLabelFontSize}px`}
          fill="var(--color-R)"
          className={styles.text}
        >
          Aucune donnée
        </text>
      </g>
    );
  };

  /**
   * Les traits de la grille (ceux de renderYTicks) sont à tick/maxValue de la
   * hauteur, pour les ticks multiples de `skip`. La graduation de droite au
   * j-ième trait vaut j × pas, avec un pas « rond » choisi pour que le haut de
   * l'axe couvre le maximum de la courbe.
   */
  const renderSecondaryCurve = (): JSX.Element => {
    if (!secondaryCurve || secondaryCurve.values.length === 0 || data.length === 0) {
      return <></>;
    }

    const color  = secondaryCurve.color ?? 'var(--color-R)';
    const yTicks = Math.ceil(maxValue);
    const yTickSpacing = availableHeight / yTicks;

    // Même règle de saut que renderYTicks : on retrouve les traits réellement dessinés.
    const ySkipValues = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
    let skip = 1;
    for (const candidate of ySkipValues) {
      if (yTickSpacing * candidate < yAxisLabelFontSize * 1.5) { skip = candidate; } else { break; }
    }

    const curveMax = Math.max(1, ...secondaryCurve.values);

    // Le haut de l'axe (position 1, soit maxValue) vaut (maxValue / skip) × pas.
    const needed = (curveMax * skip) / maxValue;
    const magnitude = Math.pow(10, Math.floor(Math.log10(needed)));
    // Un effectif est entier : jamais moins de 1 par graduation, sinon l'arrondi répète
    // « 0, 0, 1, 1, 1 » quand la courbe est basse. Elle prend alors l'échelle de gauche (0 à 5).
    const step = Math.max(skip, [1, 2, 5, 10].map(m => m * magnitude).find(v => v >= needed) ?? 10 * magnitude);
    const ratio = curveMax / maxValue;

    // Écart important : la graduation de droite = celle de gauche × k, avec k
    // « rond » (1, 2, 5 × 10ⁿ) et au moins égal à l'écart, pour que la courbe tienne.
    const multipleMode = ratio > (secondaryCurve.multipleThreshold ?? 3);
    let k = 1;
    if (multipleMode) {
      const kMagnitude = Math.pow(10, Math.floor(Math.log10(ratio)));
      k = [1, 2, 5, 10].map(m => m * kMagnitude).find(v => v >= ratio) ?? 10 * kMagnitude;
    }

    const axisMax = multipleMode ? k * maxValue : (step * maxValue) / skip;
    const labelOf = (tick: number): number => multipleMode ? tick * k : Math.round((tick / skip) * step);

    const T = colW;
    const points = secondaryCurve.values.slice(0, data.length).map((v, i) => ({
      x    : colX(i) + (T * 0.8) / 2,
      y    : dimensions.height - paddingBottom - (v / axisMax) * availableHeight,
      v,
      label: data[i].group,
    }));

    const axisX = dimensions.width - paddingRight;

    return (
      <g>
        {/* La limite de droite, tracée comme celle de gauche, dans la couleur de la courbe. */}
        <line
          x1={axisX}
          y1={paddingTop}
          x2={axisX}
          y2={dimensions.height - paddingBottom}
          stroke={color}
          strokeWidth="1"
        />

        {/* Graduations de droite, sur les mêmes traits que l'axe de gauche. */}
        {Array.from({ length: yTicks }, (_, i) => i + 1)
          .filter(tick => tick % skip === 0)
          .map(tick => {
            const y = dimensions.height - paddingBottom - (tick / maxValue) * availableHeight;
            return (
              <text key={`r-${tick}`} x={axisX + 6} y={y + 5} textAnchor="start" fill={color} fontSize={`${yAxisLabelFontSize}px`}>
                {labelOf(tick)}
              </text>
            );
          })}

        {renderAxisLabel(multipleMode ? `${secondaryCurve.name} (×${k})` : secondaryCurve.name, axisX, color, 'secondary-label')}

        <g clipPath={`url(#${clipId})`}>
        <path
          d={points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')}
          fill="none"
          stroke={color}
          strokeWidth="2"
        />

        {/* Aucun point sur la courbe : seul celui de l'abscisse survolée est dessiné (ci-dessous). */}

        {hoverIndex !== null && points[hoverIndex] && (
          <circle cx={points[hoverIndex].x} cy={points[hoverIndex].y} r={6} fill="#fff7e0" stroke={color} strokeWidth="2"
                  style={{ filter: `drop-shadow(0 0 6px ${color}) drop-shadow(0 0 10px ${color})` }} pointerEvents="none" />
        )}
        </g>
      </g>
    );
  };

  const renderDatas = (): JSX.Element => {
    switch (grapheType) {
      case 'Bar':
        return renderDatasWithBar();
      case 'Line':
        return renderDatasWithLine();
      default:
        return <></>;
    }
  };



const renderDatasWithLine = (): JSX.Element => {
  return (
    <>
      {data.map((group, groupIndex) => {
        if (group.series.length === 0) {
          return renderNoData();
        }
        const seriesPoints = group.series.map((series, seriesIndex) => {
          if (seriesInvisibility[seriesIndex]) {
            return null;
          }
          const points = data.map((group, groupIndex) => {
            const x = colX(groupIndex) + (colW * 0.8) / 2;
            let yValue = 0;
            if (cumul) {
              let cumulativeValue = 0;
              for (let i = 0; i <= seriesIndex; i++) {
                if (!seriesInvisibility[i]) {
                  const seriesData = group.series[i];
                  cumulativeValue += seriesData ? seriesData.value : 0;
                }
              }
              yValue = (cumulativeValue / maxValue) * availableHeight;
            } else {
              const seriesData = group.series[seriesIndex];
              yValue = seriesData ? (seriesData.value / maxValue) * availableHeight : 0;
            }
            const y = dimensions.height - paddingBottom - yValue;
            return { x, y };
          });
          return points;
        });

        const paths = seriesPoints.map((points, seriesIndex) => {
          if (!points) {
            return null;
          }
          const pathData = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
          return (
            <g key={`series-${seriesIndex}`}>
              <path
                d={pathData}
                fill="none"
                stroke={colors[seriesIndex % colors.length]}
                strokeWidth="1"
                className={styles.line}
              />
              {points.map((point, i) => (
                <circle
                  key={`dot-${seriesIndex}-${i}`}
                  cx={point.x}
                  cy={point.y}
                  r={4}
                  fill={colors[seriesIndex % colors.length]}
                  stroke={textColor}
                  strokeWidth="1"
                  onClick={(e) => onClick?.(e, i, seriesInvisibility[seriesIndex])}
                  onMouseEnter={(e) => onMouseEnter?.(e, group.group, group.series[seriesIndex].name, group.series[seriesIndex].value)}
                  onMouseLeave={(e) => onMouseLeave?.(e, group.group, group.series[seriesIndex].name, group.series[seriesIndex].value)}
                />
              ))}
            </g>
          );
        }).filter(Boolean);

        return <g key={`group-${groupIndex}`}>{paths}</g>;
      })}
    </>
  );
};



const renderDatasWithBar = (): JSX.Element => {
  const visibleSeriesCount = data.length > 0 ? data[0].series.filter((_, seriesIndex) => !seriesInvisibility[seriesIndex]).length : 0;
  const T = colW;
  const k = 0.1;
  // Empilées, les séries ne forment qu'UNE barre : même largeur qu'une série seule.
  const nbBarres = cumul ? 1 : visibleSeriesCount;
  const b = T / (nbBarres + (nbBarres + 3) * k);
  const e = k * b;

  return (
    <>
      {data.map((group, groupIndex) => {
        if (groupIndex < premierVisible || groupIndex > dernierVisible) {
          return null;
        }
        if (group.series.length === 0) {
          return renderNoData();
        }
        const bars = group.series.map((series, seriesIndex) => {
          if (seriesInvisibility[seriesIndex]) {
            return null;
          }
          const seriesData = group.series[seriesIndex];
          let x, y, yValue, height;
          if (cumul) {
            let cumulativeValue = 0;
            for (let i = 0; i <= seriesIndex; i++) {
              if (!seriesInvisibility[i]) {
                const seriesData = group.series[i];
                cumulativeValue += seriesData ? seriesData.value : 0;
              }
            }
            yValue = (cumulativeValue / maxValue) * availableHeight;
            height = seriesData ? (seriesData.value / maxValue) * availableHeight : 0;
            x = colX(groupIndex);
            y = dimensions.height - paddingBottom - yValue;
          } else {
            const visibleIndex = group.series.slice(0, seriesIndex + 1).filter((_, index) => !seriesInvisibility[index]).length - 1;
            x = colX(groupIndex) + (visibleIndex + 1) * e + visibleIndex * b;
            yValue = seriesData ? (seriesData.value / maxValue) * availableHeight : 0;
            y = dimensions.height - paddingBottom - yValue;
            height = yValue;
          }
          return (
            <rect
              key={`bar-${groupIndex}-${seriesIndex}`}
              x={cumul ? x + e : x}
              y={y}
              width={b}
              height={height}
              fill={colors[seriesIndex % colors.length]}
              className={styles.bar}
              style={groupIndex === hoverIndex
                ? { filter: 'brightness(1.7) drop-shadow(0 0 6px ' + colors[seriesIndex % colors.length] + ')' }
                : highlightIndices?.includes(groupIndex)
                  ? { filter: 'brightness(1.6) drop-shadow(0 0 5px ' + colors[seriesIndex % colors.length] + ')' }
                  : undefined}
              pointerEvents="none"
              onClick={(e) => onClick?.(e, groupIndex, seriesInvisibility[seriesIndex])}
              onMouseEnter={(e) => onMouseEnter?.(e, group.group, series.name, series.value)}
              onMouseLeave={(e) => onMouseLeave?.(e, group.group, series.name, series.value)}
            >
            </rect>
          );
        }).filter(Boolean);
        return <g key={`group-${groupIndex}`}>{bars}</g>;
      })}
    </>
  );
};


  return (
    <div ref={graphRef} style={{ width: '100%', height: '100%' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        width="100%"
        height="100%"
        aria-labelledby={title}
        role="img"
        onMouseLeave={() => { setTooltip(null); survoler(null); }}
      >
        {renderTitles()}
        {renderLegend(dimensions.width - 125, 5)}
        {renderAxes()}
        {yAxisLabel && renderAxisLabel(yAxisLabel, paddingLeft - 2, textColor, 'primary-label')}
        {renderYTicks()}
        {renderXTicks()}
        <defs>
          <clipPath id={clipId}>
            <rect x={paddingLeft} y={0} width={availableWidth} height={Math.max(0, dimensions.height - paddingBottom)} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>{renderDatas()}</g>
        {renderSecondaryCurve()}
        {renderHover()}
      </svg>
    </div>
  );
};

export default CartesianGraph2;
